/**
 * M7 S9: the §3.1 contact points (spec §3.1, §16.5). Docking is refused at hostile standing (≤ −50), at wanted ≥ 1
 * (with the owner or the system's jurisdiction) and under Tethys's `dockingRefused` retaliation row, so no story
 * interaction may need a dock the story can close. `contactFor` walks the interaction's options from
 * `comms/common.json contacts` (the primary first, then the fallbacks: comms filing, neutral Mutual meetings, the
 * prisoner transport, Duarte's desk line, the archivist if `duarte_exposed`) and returns the first reachable one.
 *
 * - A `dock` option is refused for `hostile`, `wanted` (unless `lawless`), `retaliation` or `standing` (below
 *   `minStanding`); a `comms` option with a faction only when that faction is hostile (it won't talk); a `ship`
 *   option never. Flag and character-state conditions (`flagsNone`, `okaforIn`, `reyesNotIn`) rule an option out
 *   silently.
 * - The first refused dock's reason picks the generic refusal node the player hears before the fallback.
 * - `registrantsFor`: a wanted pilot can't register a reactivation (hand-over only, spec §16.5).
 *
 * Pure; standings come from the view when the worker mirrors them, else from the Career (guardian: 0).
 */
import type { Career } from '../career/types'
import { SIM_FACTION_IDS, type SimFactionId } from '../data/m2Schemas'
import { logger } from '../core/logger'
import { FACTION_IDS, type FactionId } from '../strategic/types'
import { commsConfig } from './comms'
import type { ContactOption, ContactsConfig } from './schemas'
import { storyOf } from './state'
import type { ContactInteraction, Registrant, StoryWorldView } from './types'

/** What `contactFor` reads from the worker: the player's wanted levels, and standings when mirrored. */
export interface ContactView extends Pick<StoryWorldView, 'corpFaction'> {
  /** Wanted level per faction (`law/wanted.ts wantedLevel`); absent = 0. */
  wanted: Partial<Record<FactionId, number>>
  /** The worker's mirrored standings; absent entries fall back to `career.standings` (guardian: 0). */
  standings?: Partial<Record<FactionId, number>>
}

export const CONTACT_REFUSALS = ['hostile', 'wanted', 'retaliation', 'standing', 'condition'] as const
export type ContactRefusal = (typeof CONTACT_REFUSALS)[number]

export interface ContactResolution {
  interaction: ContactInteraction
  /** The option to use (the last one when none is reachable, with `reachable` false). */
  option: ContactOption
  /** True when the primary contact is reachable. */
  primary: boolean
  reachable: boolean
  /** The options passed over, in order, and why. */
  refused: Array<{ optionId: string; reason: ContactRefusal }>
  /** The generic refusal node to play before the fallback (the first refused dock's reason). */
  refusalNode?: string
}

const SIM_FACTIONS: ReadonlySet<string> = new Set(SIM_FACTION_IDS)

function isSimFaction(f: FactionId): f is FactionId & SimFactionId {
  return SIM_FACTIONS.has(f)
}

/** The player's standing with `faction` (view first, then the Career; 0 for a faction the Career doesn't track). */
export function standingWith(career: Career, view: ContactView, faction: FactionId): number {
  const mirrored = view.standings?.[faction]
  if (mirrored !== undefined) return mirrored
  return isSimFaction(faction) ? career.standings[faction] : 0
}

function wantedWith(view: ContactView, faction: FactionId): number {
  return view.wanted[faction] ?? 0
}

/** Wanted with any faction at the docking threshold (a wanted pilot registers nothing, spec §16.5). */
export function isWanted(view: ContactView, cfg: ContactsConfig = commsConfig().contacts): boolean {
  return FACTION_IDS.some((f) => wantedWith(view, f) >= cfg.wantedLevel)
}

/** Why `option` is refused now, or null when it is reachable. */
export function optionRefusal(
  career: Career,
  view: ContactView,
  option: ContactOption,
  cfg: ContactsConfig = commsConfig().contacts,
): ContactRefusal | null {
  const story = storyOf(career)
  if (option.flagsNone.some((f) => story.flags[f] === true)) return 'condition'
  if (option.okaforIn !== undefined && (story.okaforState === undefined || !option.okaforIn.includes(story.okaforState))) return 'condition'
  if (story.reyesState !== undefined && option.reyesNotIn.includes(story.reyesState)) return 'condition'
  const f = option.faction
  if (option.channel === 'ship' || f === undefined) return null
  const hostile = standingWith(career, view, f) <= cfg.hostileStanding
  if (option.channel === 'comms') return hostile ? 'hostile' : null
  if (hostile) return 'hostile'
  if (!option.lawless) {
    if (wantedWith(view, f) >= cfg.wantedLevel) return 'wanted'
    if (option.jurisdiction !== undefined && wantedWith(view, option.jurisdiction) >= cfg.wantedLevel) return 'wanted'
  }
  if (f === cfg.retaliationFaction && story.retaliation?.rows.includes('dockingRefused') === true) return 'retaliation'
  if (option.minStanding !== undefined && standingWith(career, view, f) < option.minStanding) return 'standing'
  return null
}

/** Spec §3.1: the primary contact for `interaction`, or its first reachable fallback. */
export function contactFor(
  career: Career,
  view: ContactView,
  interaction: ContactInteraction,
  cfg: ContactsConfig = commsConfig().contacts,
): ContactResolution {
  const options = cfg.interactions[interaction]
  const refused: ContactResolution['refused'] = []
  let refusalNode: string | undefined
  for (let i = 0; i < options.length; i++) {
    const option = options[i]
    const reason = optionRefusal(career, view, option, cfg)
    if (reason === null) {
      const out: ContactResolution = { interaction, option, primary: i === 0, reachable: true, refused }
      if (refusalNode !== undefined) out.refusalNode = refusalNode
      return out
    }
    refused.push({ optionId: option.id, reason })
    if (refusalNode === undefined && option.channel === 'dock' && reason !== 'condition') refusalNode = cfg.refusalNodes[reason]
  }
  const last = options[options.length - 1]
  logger.warn(`story: no reachable contact for ${interaction}; using ${last.id}`)
  const out: ContactResolution = { interaction, option: last, primary: false, reachable: false, refused }
  if (refusalNode !== undefined) out.refusalNode = refusalNode
  return out
}

/**
 * Who may register a restarted derelict (spec §16.5): a restart title for a pilot without a corporation who isn't
 * wanted; a corporation station when a corporation exists (the corporation isn't wanted); a hand-over always.
 */
export function registrantsFor(view: ContactView, cfg: ContactsConfig = commsConfig().contacts): Registrant[] {
  const out: Registrant[] = []
  if (view.corpFaction === null && !isWanted(view, cfg)) out.push('title')
  if (view.corpFaction !== null) out.push('corporation')
  out.push('handover')
  return out
}
